import { Box, Chip, Grid, Stack, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import GlobalButton from "../../../components/button";
import DynamicTable from "../../../components/dynamicTable";
import AppHeader from "../../../components/header";
import Label from "../../../components/label";
import AppLoader from "../../../components/loader";
import TextInput from "../../../components/textInput";
import {
  agentMarkOffDays,
  agentRemoveOffDays,
  getAgentOffDays,
} from "../../../services/AgentApi";
import { handleOpenSnackBar } from "../../../utils/helper";
import moment from "moment";
import { dayjs } from "dayjs";

const useStyle = {
  datePickerStyle: {
    width: "100%",
    "&.MuiFormControl-root .MuiInputBase-root": {
      borderRadius: "4px",
      backgroundColor: "#d4ecff87",
    },
  },
};

const tableHeader = ["Date", "Day", "Reason", "Actions"];

const displayRows = ["agent_off_date", "off_day", "reason", "actions"];

const AgentOffDaysPage = ({}) => {
  const params = useParams();
  const [reason, setReason] = useState("");
  const [offDays, setOffDays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [singlesOffDays, setSingleOffDays] = useState([]);
  const [dumyDate, setDumyDate] = useState(null);


  useEffect(() => {
    if (params?.agentId) {
      fetchAgentOffDays();
    }
  }, [params?.agentId]);

  const fetchAgentOffDays = async () => {
    try {
      setIsLoading(true);
      const response = await getAgentOffDays(params?.agentId);
      if (response.data.status === "success") {
        const data = response.data.data;
        const update = data?.offDays?.map((x) =>
          moment(x.date).format("YYYY-MM-DD")
        );
        setOffDays(data.offDays);
        setSingleOffDays(update);
        setUserDetail(data);
      }
    } catch (error) {
      console.warn(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDateSelection = (date) => {
    if (!date) return;

    // Fix timezone issue by formatting date in 'YYYY-MM-DD'
    const dateString = moment(date.$d).format("YYYY-MM-DD");

    if (offDays.some((d) => moment(d.date).format("YYYY-MM-DD") === dateString))
      return;

    if (selectedDates.includes(dateString)) {
      setSelectedDates(selectedDates.filter((d) => d !== dateString));
    } else {
      setSelectedDates([...selectedDates, dateString]);
    }
  };

  const markOffDays = async () => {
    if (selectedDates.length === 0)
      return handleOpenSnackBar("error", "Please select at least one date");
    if (!reason) return handleOpenSnackBar("error", "Reason is required");

    const newDates = selectedDates.filter(
      (date) => !singlesOffDays.includes(date)
    );

    if (newDates.length === 0)
      return handleOpenSnackBar(
        "error",
        "Selected dates are already marked as off days"
      );

    const payload = {
      dates: selectedDates,
      reason,
    };


    // return

    try {
      setIsLoading(true);
      const response = await agentMarkOffDays(params?.agentId, payload);
      if (response.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        setSelectedDates([]);
        fetchAgentOffDays();
        setReason("");
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveOffDay = async (row) => {
    try {
      setIsLoading(true);
      const response = await agentRemoveOffDays(userDetail?._id, {
        date: row.date,
      });
      if (response.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        fetchAgentOffDays();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const returnSortedDates = offDays.sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );


  return (
    <AppHeader>
      <AppLoader isVisible={isLoading} />
      <Typography
        variant="primaryText"
        component={"h1"}
        fontSize={{ xs: "1.5rem", md: "2.125rem" }}
        gutterBottom
      >
        Mark Off Days For Agent {userDetail?.username}
      </Typography>

      <Grid container spacing={2} alignItems={"end"}>
        <Grid item xs={12} sm={5}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Label title={"Select off days"} />
            <MobileDatePicker
              // label="Select Off Days"
              value={dumyDate}
              onChange={(newValue) => {
                toggleDateSelection(newValue);
                setDumyDate(newValue);
              }}
              onClose={() => setDumyDate(null)}
              shouldDisableDate={(date) => {
                const day = date.day(); // 0 = Sunday, 6 = Saturday
                const formattedDate = date.format("YYYY-MM-DD"); // Format to match offDays array

                // Agar date weekend hai ya pehle se off days me hai to disable
                return (
                  day === 0 ||
                  day === 6 ||
                  singlesOffDays.includes(formattedDate)
                );
              }}
              sx={useStyle.datePickerStyle}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={5}>
          <Label title={"Add reason"} />
          <TextInput
            variant="outlined"
            id={"agent-reason"}
            type={"text"}
            required
            placeholder={"Enter reason for off days"}
            value={reason}
            name={"reason"}
            onChange={(e) => setReason(e.target.value)}
            style={{
              "& .MuiInputBase-root": {
                borderRadius: "4px",
                backgroundColor: "#d4ecff87",
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <GlobalButton
            variant={"custom_gradient"}
            handleClickBtn={markOffDays}
            title={"Mark Off Days"}
            style={{ height: 50, borderRadius: "4px" }}
          />
        </Grid>
        {selectedDates?.length > 0 && (
          <Grid item xs={12}>
            <Box my={2}>
              <Typography>Selected Dates</Typography>
              <Stack
                mt={1}
                gap={1}
                direction={"row"}
                alignItems={"center"}
                flexWrap={"wrap"}
              >
                {selectedDates.map((date, index) => (
                  <Chip
                    key={index}
                    label={moment(date).format("DD.MM.YY")}
                    color="primary"
                    onDelete={() =>
                      setSelectedDates(selectedDates.filter((d) => d !== date))
                    }
                  />
                ))}
              </Stack>
            </Box>
          </Grid>
        )}
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Agent Off Days
      </Typography>

      <DynamicTable
        tableData={returnSortedDates || []}
        displayRows={displayRows}
        tableHeader={tableHeader}
        showDeleteIcon
        handleDelete={handleRemoveOffDay}
      />
    </AppHeader>
  );
};

export default AgentOffDaysPage;
